/**
 * Admin: Founder Letter Editor + A/B Split-Test
 * ---------------------------------------------------------------------------
 * Unified editor for the founder's opening blurb. Variant A is the LIVE
 * letter (used by both the landing-page tile AND the welcome email when
 * A/B is disabled). Variant B is a challenger — when A/B is ENABLED,
 * every new signup gets randomly assigned A or B, and conversion stats
 * are attributed to each variant.
 *
 * Backend endpoints:
 *   - GET  /api/founder-letter                              (public, variant A only)
 *   - PUT  /api/admin/founder-letter                        (variant A)
 *   - POST /api/admin/founder-letter/reset
 *   - GET  /api/admin/founder-letter/variants               (both variants + AB flag)
 *   - PUT  /api/admin/founder-letter/variants/b
 *   - POST /api/admin/founder-letter/variants/toggle
 *   - GET  /api/admin/founder-letter/variants/stats
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  PencilSimple, Eye, ArrowClockwise, FloppyDisk, User,
  Envelope, House, ArrowsClockwise, Flask, Trophy, ChartLineUp,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

const _EMPTY_VARIANT = {
  overline: "A note from the founder",
  paragraphs: ["", "", "", ""],
  signature_name: "Mike",
  signature_title: "Founder · Sioux Falls, SD",
};

export default function AdminFounderLetterPage() {
  const { user } = useAuth();
  const [activeVariant, setActiveVariant] = useState("a"); // "a" | "b"
  const [variantA, setVariantA] = useState(_EMPTY_VARIANT);
  const [variantB, setVariantB] = useState(null);          // null = never created
  const [abEnabled, setAbEnabled] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState("landing");
  const [toggling, setToggling] = useState(false);

  const _padParas = (arr) => {
    const p = Array.isArray(arr) ? arr.slice(0, 6) : [];
    while (p.length < 4) p.push("");
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [v, s] = await Promise.all([
        client.get("/admin/founder-letter/variants"),
        client.get("/admin/founder-letter/variants/stats"),
      ]);
      setAbEnabled(v.data.enabled);
      setVariantA({
        overline: v.data.variant_a?.overline || "A note from the founder",
        paragraphs: _padParas(v.data.variant_a?.paragraphs),
        signature_name: v.data.variant_a?.signature_name || "Mike",
        signature_title: v.data.variant_a?.signature_title || "Founder",
      });
      if (v.data.variant_b) {
        setVariantB({
          overline: v.data.variant_b.overline || "A note from the founder",
          paragraphs: _padParas(v.data.variant_b.paragraphs),
          signature_name: v.data.variant_b.signature_name || "Mike",
          signature_title: v.data.variant_b.signature_title || "Founder",
        });
      } else {
        setVariantB(null);
      }
      setStats(s.data);
    } catch (e) {
      toast.error("Failed to load founder letter data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentVariant = activeVariant === "b" ? (variantB || _EMPTY_VARIANT) : variantA;
  const setCurrentVariant = (updater) => {
    if (activeVariant === "b") {
      setVariantB((prev) => updater(prev || _EMPTY_VARIANT));
    } else {
      setVariantA(updater);
    }
  };

  const save = async () => {
    const cleaned = currentVariant.paragraphs.map((p) => (p || "").trim()).filter(Boolean);
    if (cleaned.length === 0) {
      toast.error("Add at least one paragraph before saving");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        overline: currentVariant.overline?.trim() || undefined,
        paragraphs: cleaned,
        signature_name: currentVariant.signature_name?.trim() || undefined,
        signature_title: currentVariant.signature_title?.trim() || undefined,
      };
      const endpoint = activeVariant === "b"
        ? "/admin/founder-letter/variants/b"
        : "/admin/founder-letter";
      await client.put(endpoint, payload);
      toast.success(`Variant ${activeVariant.toUpperCase()} saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (activeVariant === "b") {
      // Reset variant B = clear it entirely
      if (!window.confirm("Delete Variant B? A/B testing will be auto-disabled.")) return;
      setVariantB(null);
      // We can't DELETE variant B via API without a dedicated endpoint;
      // for now, clearing on the client + reloading is sufficient.
      toast.info("Variant B cleared locally — save again to persist");
    } else {
      if (!window.confirm("Reset Variant A to the default text? Your custom edits will be lost.")) return;
      try {
        await client.post("/admin/founder-letter/reset");
        toast.success("Reset to default");
        load();
      } catch (e) {
        toast.error("Reset failed");
      }
    }
  };

  const toggleAB = async () => {
    setToggling(true);
    try {
      const { data } = await client.post(
        "/admin/founder-letter/variants/toggle",
        { enabled: !abEnabled }
      );
      setAbEnabled(data.enabled);
      toast.success(data.enabled ? "A/B testing enabled" : "A/B testing disabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setToggling(false);
    }
  };

  const createVariantB = () => {
    setVariantB({ ..._EMPTY_VARIANT, paragraphs: _padParas(variantA.paragraphs) });
    setActiveVariant("b");
    toast.info("Variant B initialized from Variant A. Edit + save to persist.");
  };

  if (!user?.is_admin) {
    return (
      <div className="p-8" data-testid="admin-founder-letter-forbidden">
        <div className="text-red-400">Admin only.</div>
      </div>
    );
  }

  const displayParagraphs = currentVariant.paragraphs.filter((p) => (p || "").trim());
  const totalChars = currentVariant.paragraphs.reduce((sum, p) => sum + (p || "").length, 0);
  const nonEmptyCount = currentVariant.paragraphs.filter((p) => (p || "").trim()).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-founder-letter-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 text-xs font-semibold mb-3">
            <PencilSimple size={12} weight="fill" /> FOUNDER LETTER · EDITABLE
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Speak in your own voice
          </h1>
          <p className="text-neutral-400 mt-2 text-sm sm:text-base max-w-2xl">
            2–4 sentences about why you started FreightPilot AI. Shows on the landing page tile AND opens every trial welcome email. Split-test two versions to find your best-converting story.
          </p>
        </div>
      </div>

      {/* A/B stats + toggle */}
      <ABStatsCard
        stats={stats}
        abEnabled={abEnabled}
        toggling={toggling}
        onToggle={toggleAB}
        variantBExists={!!variantB}
      />

      {/* Variant tabs */}
      <div className="flex items-center gap-2" data-testid="variant-tabs">
        <VariantTab
          id="a"
          label="Variant A"
          active={activeVariant === "a"}
          onClick={() => setActiveVariant("a")}
          winner={stats?.winner === "a"}
          badge={abEnabled ? "50%" : "LIVE"}
        />
        {variantB ? (
          <VariantTab
            id="b"
            label="Variant B"
            active={activeVariant === "b"}
            onClick={() => setActiveVariant("b")}
            winner={stats?.winner === "b"}
            badge={abEnabled ? "50%" : "DRAFT"}
          />
        ) : (
          <button
            onClick={createVariantB}
            data-testid="variant-b-create"
            className="text-xs px-3 py-1.5 rounded-md border border-dashed border-white/15 text-neutral-400 hover:text-white hover:border-white/40"
          >
            + Add Variant B
          </button>
        )}
      </div>

      {/* Editor + preview split */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Editor */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card-surface p-5" data-testid={`founder-editor-${activeVariant}`}>
            <div className="mb-4">
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">
                Section overline
              </label>
              <input
                type="text"
                data-testid="founder-overline-input"
                value={currentVariant.overline}
                onChange={(e) => setCurrentVariant((v) => ({ ...v, overline: e.target.value }))}
                placeholder="e.g., A note from the founder"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
              />
            </div>

            {currentVariant.paragraphs.map((p, i) => (
              <div key={i} className="mb-3">
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">
                  Paragraph {i + 1} {i > 0 && <span className="text-neutral-600 normal-case">(optional)</span>}
                </label>
                <textarea
                  data-testid={`founder-paragraph-${i}`}
                  value={p}
                  onChange={(e) => {
                    const next = [...currentVariant.paragraphs];
                    next[i] = e.target.value;
                    setCurrentVariant((v) => ({ ...v, paragraphs: next }));
                  }}
                  placeholder={i === 0 ? "Hey — I'm [your name]. Before FreightPilot AI, I owned trucks myself…" : "…"}
                  rows={i === 0 ? 3 : 2}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60 leading-relaxed"
                />
              </div>
            ))}

            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/10">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">
                  Signature name
                </label>
                <input
                  type="text"
                  data-testid="founder-sig-name-input"
                  value={currentVariant.signature_name}
                  onChange={(e) => setCurrentVariant((v) => ({ ...v, signature_name: e.target.value }))}
                  placeholder="Mike"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5 block">
                  Signature title
                </label>
                <input
                  type="text"
                  data-testid="founder-sig-title-input"
                  value={currentVariant.signature_title}
                  onChange={(e) => setCurrentVariant((v) => ({ ...v, signature_title: e.target.value }))}
                  placeholder="Founder · Sioux Falls, SD"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10 gap-3">
              <div className="text-[11px] text-neutral-500">
                {nonEmptyCount}/4 paragraphs · {totalChars} chars
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="founder-reset-btn"
                  onClick={resetToDefault}
                  disabled={saving || loading}
                  className="text-xs text-neutral-400 hover:text-white disabled:opacity-50 flex items-center gap-1.5 px-3 py-2 rounded-md hover:bg-white/[0.05]"
                >
                  <ArrowsClockwise size={12} />
                  {activeVariant === "b" ? "Clear" : "Reset"}
                </button>
                <button
                  data-testid="founder-save-btn"
                  onClick={save}
                  disabled={saving || loading}
                  className="rounded-md px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-60 transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <ArrowClockwise size={14} className="animate-spin" /> Saving…
                    </>
                  ) : (
                    <>
                      <FloppyDisk size={14} weight="fill" /> Save Variant {activeVariant.toUpperCase()}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="text-[11px] text-neutral-500 leading-relaxed p-3 rounded-md bg-white/[0.02] border border-white/5">
            <div className="mb-1"><b className="text-neutral-400">Where this shows up:</b></div>
            <div className="flex items-center gap-2 mt-1.5">
              <House size={11} className="text-neutral-500" />
              Landing page — always uses Variant A ({" "}
              <a href="https://freightpilotai.ai/#founder" target="_blank" rel="noreferrer" className="text-[#FF9764] hover:text-[#FF5A1F] underline">
                freightpilotai.ai
              </a>{" "}
              founder tile)
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Envelope size={11} className="text-neutral-500" />
              Welcome email — {abEnabled ? "50/50 split between Variant A and B" : "always Variant A"}
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Eye size={14} weight="fill" /> LIVE PREVIEW · Variant {activeVariant.toUpperCase()}
            </div>
            <div className="flex items-center gap-1 rounded-md bg-white/[0.03] border border-white/10 p-0.5">
              <button
                data-testid="preview-mode-landing"
                onClick={() => setPreviewMode("landing")}
                className={`text-[11px] px-3 py-1 rounded ${
                  previewMode === "landing"
                    ? "bg-white/10 text-white"
                    : "text-neutral-500 hover:text-white"
                }`}
              >
                <House size={11} weight="fill" className="inline mr-1" /> Landing
              </button>
              <button
                data-testid="preview-mode-welcome"
                onClick={() => setPreviewMode("welcome")}
                className={`text-[11px] px-3 py-1 rounded ${
                  previewMode === "welcome"
                    ? "bg-white/10 text-white"
                    : "text-neutral-500 hover:text-white"
                }`}
              >
                <Envelope size={11} weight="fill" className="inline mr-1" /> Welcome email
              </button>
            </div>
          </div>

          {previewMode === "landing" ? (
            <LandingPreview
              overline={currentVariant.overline}
              paragraphs={displayParagraphs}
              signatureName={currentVariant.signature_name}
              signatureTitle={currentVariant.signature_title}
            />
          ) : (
            <WelcomeEmailPreview
              paragraphs={displayParagraphs}
              userFullName={user?.full_name}
              companyName={user?.company_name}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function VariantTab({ id, label, active, onClick, winner, badge }) {
  return (
    <button
      onClick={onClick}
      data-testid={`variant-tab-${id}`}
      className={`relative px-4 py-2 rounded-md text-sm font-semibold transition-colors flex items-center gap-2 ${
        active
          ? "bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 text-white"
          : "bg-white/[0.03] border border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
      }`}
    >
      {winner && <Trophy size={12} weight="fill" className="text-yellow-400" />}
      {label}
      {badge && (
        <span className="text-[9px] font-medium uppercase tracking-wider bg-white/10 border border-white/15 text-neutral-300 rounded-full px-1.5 py-0.5">
          {badge}
        </span>
      )}
    </button>
  );
}

function ABStatsCard({ stats, abEnabled, toggling, onToggle, variantBExists }) {
  if (!stats) return null;
  const va = stats.variant_a || {};
  const vb = stats.variant_b || {};
  const canEnable = variantBExists || abEnabled;

  return (
    <div
      className="card-surface p-5 border border-blue-500/25 bg-gradient-to-br from-blue-500/[0.04] to-transparent"
      data-testid="ab-stats-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/40 flex items-center justify-center shrink-0">
            <Flask size={20} weight="fill" className="text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white leading-tight">
              A/B Split Test
              <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold ${
                abEnabled
                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40"
                  : "bg-neutral-700/40 text-neutral-400 border border-white/10"
              }`}>
                {abEnabled ? "LIVE" : "OFF"}
              </span>
            </h2>
            <p className="text-xs text-neutral-400 mt-1 max-w-md">
              50/50 split between Variant A and Variant B on every new signup. Conversion tracked when trial upgrades to a paid plan.
            </p>
          </div>
        </div>
        <button
          data-testid="ab-toggle-btn"
          onClick={onToggle}
          disabled={toggling || !canEnable}
          title={!canEnable ? "Create Variant B first" : ""}
          className={`text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5 ${
            abEnabled
              ? "bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25"
              : "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
          } disabled:opacity-50`}
        >
          {toggling ? (
            <ArrowClockwise size={12} className="animate-spin" />
          ) : (
            <Flask size={12} weight="fill" />
          )}
          {abEnabled ? "Turn OFF" : "Turn ON"}
        </button>
      </div>

      {/* Stat grid */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <ABStatBlock
          label="Variant A"
          signups={va.signups || 0}
          converted={va.converted || 0}
          rate={va.conversion_rate_pct || 0}
          isWinner={stats.winner === "a"}
        />
        <ABStatBlock
          label="Variant B"
          signups={vb.signups || 0}
          converted={vb.converted || 0}
          rate={vb.conversion_rate_pct || 0}
          isWinner={stats.winner === "b"}
        />
      </div>

      {stats.winner ? (
        <div className="mt-3 flex items-center gap-2 text-[11px] flex-wrap">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${
            stats.statistically_significant
              ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
              : "bg-yellow-500/10 text-yellow-300 border-yellow-500/30"
          }`}>
            <Trophy size={11} weight="fill" />
            Variant {stats.winner.toUpperCase()} {stats.statistically_significant ? "wins" : "leading"}
          </span>
          {stats.winner_confidence && (
            <span className="text-neutral-400" data-testid="ab-confidence">
              {stats.winner_confidence === "trending" ? (
                <>Directional signal · need more samples for significance</>
              ) : (
                <>{stats.winner_confidence} confidence · p={stats.p_value?.toFixed(4) || "?"}</>
              )}
            </span>
          )}
          {stats.chi_squared !== null && stats.chi_squared !== undefined && (
            <span className="text-neutral-500 font-mono text-[10px]" data-testid="ab-chi-squared">
              χ²={stats.chi_squared}
            </span>
          )}
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-neutral-500">
          Need at least {stats.min_sample_size_for_winner || 10} signups per variant to trend, {stats.min_sample_size_for_significance || 30}+ for statistical significance (p&lt;0.05)
        </div>
      )}
    </div>
  );
}

function ABStatBlock({ label, signups, converted, rate, isWinner }) {
  return (
    <div className={`rounded-lg border p-3 ${
      isWinner
        ? "border-yellow-500/40 bg-yellow-500/5"
        : "border-white/10 bg-white/[0.02]"
    }`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
        {isWinner && <Trophy size={10} weight="fill" className="text-yellow-400" />}
        {label}
      </div>
      <div className="flex items-baseline gap-4">
        <div>
          <div className="text-xl font-semibold text-white tabular-nums">{signups}</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Signups</div>
        </div>
        <div>
          <div className="text-xl font-semibold text-emerald-300 tabular-nums">{converted}</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Converted</div>
        </div>
        <div>
          <div className={`text-xl font-semibold tabular-nums ${isWinner ? "text-yellow-300" : "text-white"}`}>
            {rate.toFixed(1)}%
          </div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Rate</div>
        </div>
      </div>
    </div>
  );
}

function LandingPreview({ overline, paragraphs, signatureName, signatureTitle }) {
  return (
    <div className="card-surface p-8 bg-[#0F0F0F]" data-testid="founder-preview-landing">
      <div className="mb-3 text-[10px] uppercase tracking-[0.18em] text-[#FF5A1F] font-semibold">
        {overline || "A note from the founder"}
      </div>
      <div className="space-y-4">
        {paragraphs.length === 0 ? (
          <div className="text-neutral-500 italic text-sm">Paragraphs empty — add some text to see the preview.</div>
        ) : (
          paragraphs.map((p, i) => (
            <p key={i} className="text-neutral-200 text-base leading-relaxed">
              {p}
            </p>
          ))
        )}
      </div>
      <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 flex items-center justify-center">
          <User size={16} weight="fill" className="text-[#FF5A1F]" />
        </div>
        <div>
          <div className="text-white text-sm font-semibold">{signatureName || "Mike"}</div>
          <div className="text-neutral-500 text-xs">{signatureTitle || "Founder"}</div>
        </div>
      </div>
    </div>
  );
}

function WelcomeEmailPreview({ paragraphs, userFullName, companyName }) {
  const firstName = (userFullName || "there").split(" ")[0];
  const companyLine = companyName ? `'${companyName}'` : "your operation";
  return (
    <div
      className="rounded-lg bg-white text-neutral-900 p-8 border border-white/10 overflow-hidden"
      data-testid="founder-preview-welcome"
    >
      <div className="border-l-4 border-[#FF5A1F] pl-3 mb-5">
        <div className="text-[10px] tracking-[0.18em] text-[#FF5A1F] font-bold uppercase">
          Welcome aboard
        </div>
        <div className="text-2xl font-semibold text-neutral-900 mt-1">FreightPilot AI</div>
      </div>
      <p className="mb-3">Hey {firstName} —</p>
      <p className="mb-3">Real quick note from me before you get lost in the app.</p>
      {paragraphs.length === 0 ? (
        <div className="text-neutral-500 italic text-sm py-2">
          (Paragraphs empty — the welcome email will show the default founder blurb.)
        </div>
      ) : (
        paragraphs.map((p, i) => (
          <p key={i} className="mb-3 leading-relaxed">{p}</p>
        ))
      )}
      <p className="mb-2 mt-4"><b>Here's what to do first</b> (3–4 min):</p>
      <ol className="pl-5 text-sm space-y-1 mb-4">
        <li>Paste your first rate confirmation into Dispatch.</li>
        <li>Connect your Gmail in Settings.</li>
        <li>Set your rate rules.</li>
      </ol>
      <div className="text-center my-5">
        <span className="inline-block px-6 py-3 rounded-full bg-[#FF5A1F] text-black font-bold text-sm">
          Open the dashboard →
        </span>
      </div>
      <p className="text-xs text-neutral-500 mt-4">
        (Welcome to {companyLine}'s new operating system.)
      </p>
    </div>
  );
}
